<template>
  <div class="downloads-list">
    <div v-if="downloads.length === 0" class="downloads-list__empty">
      {{ $t('menus.topBars.noDownloads') }}
    </div>
    <div
      v-for="download in downloads"
      :key="download.requestId"
      class="download-item"
      :class="'download-item--' + download.status.toLowerCase()"
    >
      <div
        class="download-item__header"
        :class="{ 'download-item__header--expandable': download.selectionTotalCount > 1 }"
        @click="download.selectionTotalCount > 1 && toggleExpand(download.requestId)"
      >
        <v-icon size="small" :color="statusColor(download.status)">
          {{ statusIcon(download.status) }}
        </v-icon>
        <span class="download-item__title">
          {{ downloadTitle(download) }}
        </span>
        <v-icon
          v-if="download.selectionTotalCount > 1"
          size="small"
          class="download-item__chevron"
          :class="{ 'download-item__chevron--open': isExpanded(download.requestId) }"
        >
          mdi-chevron-down
        </v-icon>
        <span v-else-if="download.createdAt" class="download-item__timestamp">
          {{ formatTimestamp(download.createdAt) }}
        </span>
      </div>

      <div
        v-if="isExpanded(download.requestId) && download.selectionTotalCount > 1"
        class="download-item__list"
      >
        <div
          v-for="(item, idx) in download.selectionNames"
          :key="idx"
          class="download-item__list-entry"
        >
          <v-icon size="x-small">
            {{ item.name === null ? 'mdi-delete' : item.type === 'folder' ? 'mdi-folder' : 'mdi-file' }}
          </v-icon>
          <span :class="{ 'download-item__list-entry--deleted': item.name === null }">
            {{ item.name ?? $t('menus.topBars.deletedItem') }}
          </span>
        </div>
        <div
          v-if="download.selectionTotalCount > download.selectionNames.length"
          class="download-item__list-more"
        >
          {{ $t('menus.topBars.moreItems', { count: download.selectionTotalCount - download.selectionNames.length }) }}
        </div>
      </div>

      <div v-if="isActive(download.status)" class="download-item__progress">
        <v-progress-linear
          :model-value="download.percentage || 0"
          :indeterminate="download.status === 'FINALIZING' && !download.percentage"
          height="6"
          rounded
          color="primary"
        />
        <div class="download-item__details">
          <span v-if="download.podCount > 1">{{ download.processedPods || 0 }}/{{ download.podCount }} files</span>
          <span v-if="download.etaSeconds">
            · ETA {{ formatEta(download.etaSeconds) }}
          </span>
        </div>
      </div>

      <div v-if="download.status === 'FAILED'" class="download-item__error">
        {{ downloadErrorMessage(download) }}
      </div>

      <div v-if="download.status === 'READY' && download.signedUrl" class="download-item__actions">
        <v-btn
          size="small"
          color="primary"
          variant="tonal"
          @click="openDownload(download.signedUrl)"
        >
          <v-icon size="small" class="mr-1">mdi-download</v-icon>
          {{ $t('menus.topBars.downloadFile') }}
        </v-btn>
        <span v-if="download.expiresAt" class="download-item__expires">
          {{ $t('menus.topBars.linkExpires', { time: formatExpires(download.expiresAt) }) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { useNotificationStore } from '@/stores/NotificationStore.js'

export default {
  name: "DownloadsList",
  data() {
    return {
      expandedIds: {},
    };
  },
  methods: {
    isActive(status) {
      return status === 'PREPARING' || status === 'FINALIZING';
    },
    toggleExpand(id) {
      this.expandedIds = { ...this.expandedIds, [id]: !this.expandedIds[id] };
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },
    downloadTitle(download) {
      if (download.status === 'FAILED') {
        return this.$t('menus.topBars.downloadFailed');
      }

      const isReady = download.status === 'READY';
      const files = download.podCount;
      const folders = download.nodeCount;

      if (files > 0 && folders > 0) {
        if (files === 1 && folders === 1) {
          return this.$t(isReady ? 'menus.topBars.readyFile1Folder' : 'menus.topBars.downloadingFile1Folder');
        }
        if (files === 1) {
          return this.$t(isReady ? 'menus.topBars.ready1FileFromFolders' : 'menus.topBars.downloading1FileFromFolders', { folders });
        }
        if (folders === 1) {
          return this.$t(isReady ? 'menus.topBars.readyFiles1Folder' : 'menus.topBars.downloadingFiles1Folder', { files });
        }
        return this.$t(isReady ? 'menus.topBars.readyFilesFromFolders' : 'menus.topBars.downloadingFilesFromFolders', { files, folders });
      }

      if (folders === 0) {
        if (files === 1 && download.filename) {
          return this.$t(isReady ? 'menus.topBars.readyFile' : 'menus.topBars.downloadingFile', { name: download.filename });
        }
        if (files === 1) {
          return this.$t(isReady ? 'menus.topBars.ready1File' : 'menus.topBars.downloading1File');
        }
        if (files > 1) {
          return this.$t(isReady ? 'menus.topBars.readyFiles' : 'menus.topBars.downloadingFiles', { count: files });
        }
      }

      return this.$t('menus.topBars.preparingDownload');
    },
    downloadErrorMessage(download) {
      if (download.messageCode) {
        return this.$replaceT(download.messageCode, download.messageParams, download.errorReason || this.$t('menus.topBars.downloadFailed'));
      }
      return download.errorReason || this.$t('menus.topBars.downloadFailed');
    },
    statusIcon(status) {
      switch (status) {
        case 'PREPARING':
        case 'FINALIZING':
          return 'mdi-progress-clock';
        case 'READY':
          return 'mdi-check-circle';
        case 'FAILED':
          return 'mdi-alert-circle';
        default:
          return 'mdi-download';
      }
    },
    statusColor(status) {
      switch (status) {
        case 'PREPARING':
        case 'FINALIZING':
          return 'primary';
        case 'READY':
          return 'success';
        case 'FAILED':
          return 'error';
        default:
          return 'default';
      }
    },
    formatEta(seconds) {
      if (!seconds || seconds < 0) return '';
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    formatExpires(expiresAtStr) {
      if (!expiresAtStr) return '';
      const date = new Date(expiresAtStr);
      return date.toLocaleString();
    },
    openDownload(url) {
      window.open(url, '_blank');
    },
  },
  computed: {
    downloads: function() {
      const notificationStore = useNotificationStore();
      return notificationStore.getDownloads.sort((a, b) => (a.createdAt - b.createdAt) * -1);
    },
  },
}
</script>

<style lang="scss" scoped>
.downloads-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.downloads-list__empty {
  padding: 16px;
  text-align: center;
  opacity: 0.6;
}

.download-item {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(128, 128, 128, 0.2);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.download-item__header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;

  &--expandable {
    cursor: pointer;
    user-select: none;
  }
}

.download-item__timestamp {
  margin-left: auto;
  font-size: 12px;
  font-weight: 400;
  opacity: 0.6;
  white-space: nowrap;
}

.download-item__title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-item__chevron {
  margin-left: auto;
  transition: transform 0.2s ease;

  &--open {
    transform: rotate(180deg);
  }
}

.download-item__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  padding-left: 4px;
}

.download-item__list-entry {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.85;

  &--deleted {
    opacity: 0.5;
    font-style: italic;
  }
}

.download-item__list-more {
  font-size: 12px;
  opacity: 0.55;
  padding-left: 22px;
}

.download-item__progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.download-item__details {
  font-size: 12px;
  opacity: 0.7;
}

.download-item__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.download-item__expires {
  font-size: 12px;
  opacity: 0.6;
}

.download-item__error {
  font-size: 13px;
  color: rgb(var(--v-theme-error));
}
</style>
